import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Dropdown } from "semantic-ui-react";

import { DATE_FORMATS_FNS, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES, CHECKLIST_TYPES, STATUS_IDENTIFIERS } from "models";
import {
  useAddPendingChecklistMutation,
  useGroupedChecklistDropdownOptions
} from "modules/AppointmentDetails/components/Interventions/components/InterventionChecklists/components/PendingChecklists/hooks";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { TFunction } from "i18next";
import type { Appointment, Checklist, DealerLocation, Intervention } from "models";

type AddPendingChecklistProps = {
  intervention: Intervention;
  nextOrdering: number;
  checklists?: Checklist[];
  appointment?: Appointment;
};

const isChecklistAlreadyPendingOnIntervention = (checklist: Checklist, intervention: Intervention, t: TFunction) => {
  if (intervention.pending_checklists?.some(pc => pc.checklist_id === checklist.id)) {
    toast.error(t("v8_this_checklist_is_already_added", "This checklist is already pending for this intervention"));
    return true;
  }

  return false;
};

const isChecklistAlreadyPendingElsewhereOrCompleted = (checklist: Checklist, appointment: Appointment, t: TFunction) => {
  if (checklist.template?.multi_use_enabled) return false;

  const pendingFor = appointment.interventions?.find(i => i.pending_checklists?.some(pc => pc.checklist_id === checklist.id));
  if (pendingFor) {
    toast.error(
      t("v8_this_checklist_can_be_done_only_once_already_pending_for", "this checklist can be done only once and is already pending for {{interventionName}}", {
        interventionName: pendingFor.title
      })
    );
    return true;
  }

  if (appointment.car_check_events?.some(ce => ce.checklist_id === checklist.id)) {
    toast.error(t("v8_this_checklist_can_be_done_only_once", "This checklist can be done only once"));
    return true;
  }

  return false;
};

const isChecklistNotDoableNow = (checklist: Checklist, appointment: Appointment, location: DealerLocation, t: TFunction) => {
  if (checklist.template?.skip_update_appointment_status) return false;

  const lastChecklistType = appointment.car_check_events?.at(0)?.checklist_type ?? CHECKLIST_TYPES.Regular;

  if (checklist.checklist_type === CHECKLIST_TYPES.Regular) {
    if (
      ![STATUS_IDENTIFIERS.NewCarStatus, STATUS_IDENTIFIERS.CarInShop, STATUS_IDENTIFIERS.AannameOKStatus].includes(appointment.appointment_status_identifier) &&
      (![STATUS_IDENTIFIERS.CarCheckStartedStatus, STATUS_IDENTIFIERS.CarCheckStatus, STATUS_IDENTIFIERS.HandleCheckIn].includes(
        appointment.appointment_status_identifier
      ) ||
        ![CHECKLIST_TYPES.Regular, CHECKLIST_TYPES.Warranty].includes(lastChecklistType))
    ) {
      toast.error(t("v8_mechanics_cannot_perform_this_checklist_anymore_on_this_car", "Mechanics cannot perform this checklist anymore on this car"));
      return true;
    }
  }

  if (checklist.checklist_type === CHECKLIST_TYPES.Info) {
    if (
      ![STATUS_IDENTIFIERS.NewCarStatus, STATUS_IDENTIFIERS.CarInShop, STATUS_IDENTIFIERS.AannameOKStatus, STATUS_IDENTIFIERS.DiagnoseStatus].includes(
        appointment.appointment_status_identifier
      ) &&
      (![STATUS_IDENTIFIERS.CarCheckStartedStatus, STATUS_IDENTIFIERS.CarCheckStatus].includes(appointment.appointment_status_identifier) ||
        ![CHECKLIST_TYPES.Regular, CHECKLIST_TYPES.Info, CHECKLIST_TYPES.Warranty].includes(lastChecklistType))
    ) {
      const statusName = location.statuses?.find(s => s.identifier === STATUS_IDENTIFIERS.DiagnoseStatus)?.name || "";
      toast.error(t("v8_mechanics_can_ony_perform_this_checklist_in", "Mechanics can only perform this checklist in {{statusName}}", { statusName }));
      return true;
    }
  }

  if (checklist.checklist_type === CHECKLIST_TYPES.Quality) {
    if (
      ![
        STATUS_IDENTIFIERS.CarReadyStatus,
        STATUS_IDENTIFIERS.CarOkPlusRepairOverivew,
        STATUS_IDENTIFIERS.QualityCheckStatus,
        STATUS_IDENTIFIERS.QualityCheckPlusRepairOverview
      ].includes(appointment.appointment_status_identifier) &&
      (appointment.appointment_status_identifier !== STATUS_IDENTIFIERS.CarCheckStartedStatus || lastChecklistType !== CHECKLIST_TYPES.Quality)
    ) {
      const statusName = location.statuses?.find(s => s.identifier === STATUS_IDENTIFIERS.CarReadyStatus)?.name || "";
      toast.error(t("v8_mechanics_can_ony_perform_this_checklist_in", "Mechanics can only perform this checklist in", { statusName }));
      return true;
    }
  }

  return false;
};

export const AddPendingChecklist = ({ intervention, nextOrdering, checklists, appointment }: AddPendingChecklistProps) => {
  const { t } = useTranslation();

  const location = useAppointmentLocation();
  const { mutate: addPendingChecklistMutation } = useAddPendingChecklistMutation();
  const canAddPendingChecklist = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.InterventionPendingChecklist);
  const groupedChecklistDropdownOptions = useGroupedChecklistDropdownOptions(checklists || []);

  const [selectedChecklistId, setSelectedChecklistId] = useState<number>(0);
  const selectedChecklistValue = selectedChecklistId > 0 ? selectedChecklistId : "";

  if (!canAddPendingChecklist || !appointment || !location) return null;

  const canAddSelectedChecklist = (selectedChecklist: Checklist) => {
    if (isChecklistAlreadyPendingOnIntervention(selectedChecklist, intervention, t)) return false;
    if (isChecklistAlreadyPendingElsewhereOrCompleted(selectedChecklist, appointment, t)) return false;
    if (isChecklistNotDoableNow(selectedChecklist, appointment, location, t)) return false;

    return true;
  };

  const handleAddItem = () => {
    if (selectedChecklistId) {
      const selectedChecklist = checklists?.find(cl => cl.id === selectedChecklistId);

      if (selectedChecklist && canAddSelectedChecklist(selectedChecklist)) {
        setSelectedChecklistId(0);
        addPendingChecklistMutation({
          checklist_id: selectedChecklist.id,
          appointment_id: intervention.appointment_id,
          checklist_name: selectedChecklist.name,
          intervention_id: intervention.id,
          ordering: nextOrdering,
          created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
          updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat)
        });
      }
    }
  };

  return (
    <div className="add-checklists-container">
      <Dropdown
        fluid
        selection
        clearable
        search
        selectOnBlur={false}
        value={selectedChecklistValue}
        disabled={!canAddPendingChecklist}
        options={groupedChecklistDropdownOptions}
        placeholder={t("v8_select_mandatory_checklists", "Select mandatory checklists")}
        onChange={(_e, { value }) => setSelectedChecklistId(typeof value === "number" ? value : 0)}
      />
      <Button type="button" color="green" className="add-button" onClick={handleAddItem}>
        {t("v8_add", "Add")}
      </Button>
    </div>
  );
};
